import FileDeliveryService from '../../../apiclients/FileDeliveryService';
import ContentService from '../../../apiclients/ContentService';
import * as _ from 'lodash';
import AppContext from '../../../AppContext';
import { canEditCurrentContent } from '../../../helpers';

const MAX_UNDO_STEPS = 50;

// 30 minutes
const TOKEN_EXPIRE_TIME = 30 * 60 * 1000;
class ApiClient {
    /**
     * @param {object} environment configuration
    
     * @param {string} contentId (Optional)
     * @param {string} groupId (Optional)
     * @param {number} version
     */

    constructor(contentId, groupId, version = -1, contentSubclass, source) {
        this.contentId = contentId;
        this.groupId = groupId;
        this.version = version;
        this.contentSubclass = contentSubclass;
        this.canEdit = false;

        this.saving = false;
        this.data = {};
        this._history = [];
        this._urlCache = {};
        this.readOnly = false;
        this.source = source;
    }

    resetAttributes(contentId, groupId, version = -1, contentSubclass) {
        this.contentId = contentId;
        this.groupId = groupId;
        this.version = version;
        this.contentSubclass = contentSubclass;

        this.saving = false;
        this.data = {};
        this._history = [];
        this._urlCache = {};
        this.readOnly = false;
    }

    /**
     * Checks wether the document has been synced to db at least once.
     * NOTE: does not mean object is up to date
     */
    initialized() {
        return this.contentId !== undefined && this.version !== -1;
    }

    getData() {
        return this.data;
    }

    setReadOnlyMode(readOnly) {
        this.readOnly = readOnly;
    }

    getDateUpdated() {
        return this.dateUpdated;
    }

    getMeetingDate() {
        return this.meetingDate;
    }

    getCurrentLanguage() {
        return this.currentLanguage;
    }

    getContent = (contentId) => {
        const apiClient = this;

        let request;
        if (this.readOnly) {
            request = FileDeliveryService.getContent(contentId, undefined, undefined, undefined, this.source);
        } else {
            request = ContentService.getContent(contentId, apiClient.groupId);
        }
        return request;
    };

    /**
     * @return Promise
     */
    load() {
        if (this.saving) return Promise.resolve(this.data);
        const apiClient = this;

        let query;
        if (this.readOnly) {
            query = FileDeliveryService.getContent(apiClient.contentId);
        } else {
            query = ContentService.getContent(apiClient.contentId, apiClient.groupId);
        }

        return query
            .then(function (data) {
                return apiClient._readJSONResponse(data);
            })
            .catch((err) => {
                console.log(err.message);
                return Promise.reject(err);
            });
    }

    /**
     * @return Promise
     */
    checkShouldUpdate() {
        if (this.saving) return false;
        const apiClient = this;
        return ContentService.contentVersionById(this.contentId, this.groupId)
            .then(function (data) {
                if (data !== apiClient.version) {
                    return true;
                } else {
                    return false;
                }
            })
            .catch((err) => {
                console.log(err.message);
                return true;
            });
    }

    setCurrentLanguage(language) {
        // console.log('change lang');
        if (language !== this.currentLanguage) {
            //Clear history
            this._clearHistory();
        }
        this.currentLanguage = language;

        if (this.readOnly) return Promise.resolve(this.data);

        if (this.initialized()) {
            return this.load();
        } else {
            return this.data;
        }
    }

    setName(name, language) {
        this._enforceInitialized();
        if (this.readOnly) return Promise.resolve(this.data);
        const apiClient = this;
        return ContentService.setContentName(apiClient.contentId, language ? language : apiClient.currentLanguage, name, this.groupId)
            .then(function (data) {
                return apiClient._readJSONResponse(data);
            })
            .catch((err) => console.log(err.message));
    }

    getName() {
        if (this.data) {
            if (this.data.names) {
                if (this.data.names[this.currentLanguage]) {
                    return this.data.names[this.currentLanguage];
                } else if (this.data.names['default']) {
                    return this.data.names['default'];
                }
            }
        }
    }

    setGroupId(groupId) {
        this._enforceInitialized();
        if (this.readOnly) return Promise.resolve(this.data);
        const apiClient = this;
        return ContentService.setContentGroup(apiClient.contentId, groupId)
            .then(function (data) {
                return apiClient._readJSONResponse(data);
            })
            .catch((err) => console.log(err.message));
    }

    removeGroupId(groupId) {
        this._enforceInitialized();
        if (this.readOnly) return Promise.resolve(this.data);
        const apiClient = this;
        return ContentService.removeContentGroup(apiClient.contentId)
            .then(function (data) {
                return apiClient._readJSONResponse(data);
            })
            .catch((err) => console.log(err.message));
    }

    getGroupId() {
        if (this.data) {
            return this.data.groupId;
        }
    }

    getPromoted() {
        if (this.data) {
            return this.data.promoted;
        }
    }

    getSectionFilterStyle() {
        let appContext = new AppContext();
        let platformConfig = appContext.getPlatformConfig();
        if (platformConfig === undefined) return undefined;
        return platformConfig.sectionFilter.style;
    }

    /**
     *
     * @param {*} name
     * @param {*} language
     * @return Promise
     */
    createLocalization(name, language) {
        if (this.readOnly) return Promise.resolve(this.data);
        let apiClient = this;
        this._clearHistory();
        // Existing metadocument
        if (this.contentId !== undefined) {
            return ContentService.createContentLocalization(this.contentId, language, name, this.groupId)
                .then(function (data) {
                    return apiClient._readJSONResponse(data);
                })
                .catch((err) => console.log(err.message));
        } else {
            // New metadocument
            return ContentService.createMetadocument(name, language, this.groupId)
                .then(function (data) {
                    return apiClient._readJSONResponse(data);
                })
                .catch((err) => {
                    console.log(err.message);
                });
        }
    }

    /**
     *
     * @param {*} name
     * @return Promise
     */
    deleteLocalization(language) {
        if (this.readOnly) return Promise.resolve(this.data);
        let apiClient = this;
        this._clearHistory();
        return ContentService.removeContentLocalization(this.contentId, language, this.groupId)
            .then(function (data) {
                return apiClient._readJSONResponse(data);
            })
            .catch((err) => console.log(err.message));
    }

    delete() {
        if (this.readOnly) return Promise.resolve(this.data);
        this._enforceInitialized();
        this._clearHistory();
        let apiClient = this;

        // TODO: crida delete metadocument
        return ContentService.deleteContent(this.contentId, this.groupId)
            .then(function (data) {
                console.log('delete response', data);
                return apiClient._readJSONResponse(data);
            })
            .catch((err) => console.log(err.message));
    }

    _generateFileURL(fileId) {
        return FileDeliveryService.getFileURL(fileId);
    }

    getFileURL(fileId) {
        if (this._urlCache[fileId] === undefined) {
            this._urlCache[fileId] = {
                url: this._generateFileURL(fileId),
                date: new Date(),
            };
        } else if (new Date() - this._urlCache[fileId].date >= TOKEN_EXPIRE_TIME) {
            this._urlCache[fileId] = {
                url: this._generateFileURL(fileId),
                date: new Date(),
            };
        }
        return this._urlCache[fileId].url;
    }

    publish() {
        if (this.readOnly) return Promise.resolve(this.data);
        this._enforceInitialized();

        const apiClient = this;
        return ContentService.publishResource(apiClient.contentId, this.groupId)
            .then(function (data) {
                return apiClient._readJSONResponse(data);
                //console.log(data);
            })
            .catch((err) => console.log(err.message));
    }

    getContentsDefinitions() {
        this._enforceInitialized();
        return ContentService.getContentDefinitions()
            .then(function (data) {
                console.log(data);
            })
            .catch((err) => console.log(err.message));
    }

    getPublishedVersion() {
        this._enforceInitialized();

        const apiClient = this;

        return FileDeliveryService.getContent(apiClient.contentId)
            .then(function (data) {
                console.log(data);
            })
            .catch((err) => console.log(err.message));
    }

    _clearHistory() {
        this._history = [];
    }

    _addHistoryStep() {
        this._history.push(_.cloneDeep(this.data));
        if (this._history.length > MAX_UNDO_STEPS) {
            this._history = this._history.slice(this._history.length - MAX_UNDO_STEPS);
        }
    }

    _enforceInitialized() {
        if (this.contentId === undefined || this.version === -1) {
            throw new Error('Content not initialized');
        }
    }

    _readJSONResponse(response) {
        // Keep track of internal needed parameters
        this.contentId = response.contentId;
        this.version = response.version;
        this.groupId = response.groupId;
        this.objectType = response.objectType;
        this.canEdit = canEditCurrentContent(response);

        try {
            this.dateUpdated = new Date(response.dateUpdated);
        } catch (err) {}
        try {
            this.meetingDate = new Date(response.date);
        } catch (err) {}

        this.data = response;
        this._addHistoryStep();
        return this.data;
    }

    checkFileAvailable(fileId) {
        try {
            return FileDeliveryService.getFile(fileId, true)
                .then(() => true)
                .catch(() => false);
        } catch (err) {
            return Promise.resolve(false);
        }
    }
}

export class MetadocumentApiClient extends ApiClient {
    /**
     * @param {object} environment configuration
    
   
     * @param {string} contentId (Optional)
     * @param {string} groupId (Optional)
     * @param {number} version
     */

    constructor(contentId, groupId, version = -1, source) {
        super(contentId, groupId, version, 'metadocument', source);
    }

    setSections(sectionStructure) {
        this._enforceInitialized();
        if (this.saving) return Promise.resolve(this.data);
        let apiClient = this;
        this.saving = true;
        return ContentService.setContentSections(this.contentId, this.currentLanguage, this.groupId, sectionStructure, this.version)
            .then(function (data) {
                apiClient.saving = false;
                return apiClient._readJSONResponse(data);
            })
            .catch((err) => {
                return apiClient.load();
            });
        // TODO: crida add section metadocument
    }

    // Returns current language sections or default language ones
    // Warning! it may change currentlanguage if it is not valid!!
    getSections() {
        // If metadocument is localized for that language
        if (this.data.content[this.currentLanguage] === undefined) {
            this.setCurrentLanguage(this.data.defaultLanguage);
        }
        return this.data.content[this.currentLanguage].sections;
    }

    undo() {
        if (!this.saving && this._history.length > 1) {
            // Discard current
            this._history.pop();

            let step = this._history.pop();
            return this.setSections(step.content[this.currentLanguage].sections);
        } else {
            return Promise.resolve(this.data);
        }
    }

    isMeeting() {
        return this.objectType === 'mtg';
    }

    fetchNotesAndAttachments() {
        if (this.saving) return false;
        const apiClient = this;

        return ContentService.getContent(apiClient.contentId, apiClient.groupId)
            .then(function (response) {
                apiClient.version = response.version;
                return apiClient._readJSONResponse(response);
            })
            .catch((err) => {
                console.log('fetchNotesAndAttachments error', err.message);
                return '';
            });
    }

    setNotes(notes) {
        this._enforceInitialized();
        if (this.saving) return Promise.resolve(this.data);
        let apiClient = this;
        this.saving = true;
        return ContentService.setMtgNotes(this.contentId, notes, this.version)
            .then(function (data) {
                apiClient.saving = false;
                return apiClient._readJSONResponse(data);
            })
            .catch((err) => {
                return apiClient.load();
            });
    }

    setContentDefinition(newContentDefinition) {
        this._enforceInitialized();
        if (this.saving) return Promise.resolve(this.data);
        let apiClient = this;
        this.saving = true;
        return ContentService.setContentDefinition(this.contentId, newContentDefinition)
            .then(function (data) {
                apiClient.saving = false;
                return apiClient._readJSONResponse(data);
            })
            .catch((err) => {
                return apiClient.load();
            });
    }

    setPromoted(promoted) {
        this._enforceInitialized();
        if (this.saving) return Promise.resolve(this.data);
        let apiClient = this;
        this.saving = true;
        let newContentDefinition = {
            images: this.data.images,
            properties: this.data.properties,
            promoted: promoted,
            version: this.version,
        };
        return ContentService.setContentDefinition(this.contentId, newContentDefinition)
            .then(function (data) {
                apiClient.saving = false;
                return apiClient._readJSONResponse(data);
            })
            .catch((err) => {
                return apiClient.load();
            });
    }

    async uploadAttachment(droppedFile) {
        this._enforceInitialized();
        if (this.saving) return Promise.resolve(this.data);
        let apiClient = this;
        this.saving = true;
        try {
            let data = await ContentService.uploadAttachment(this.contentId, droppedFile.name, droppedFile.type);
            // console.log('begin fileUpload');
            await this.uploadFileToS3(droppedFile, data);
            // console.log('fileUploaded!');
            apiClient.saving = false;
        } catch (err) {
            console.log('Error', err);
            return apiClient.load();
        }
    }

    uploadFileToS3(droppedFile, data) {
        return new Promise((resolve, reject) => {
            let reader = new FileReader();
            let xhr = new XMLHttpRequest();

            xhr.addEventListener('load', function (response) {
                // console.log('ok');
                resolve('ok');
            });

            xhr.addEventListener('error', function (evt) {
                console.log(droppedFile.name + ' error transfering file!');
                reject(evt);
            });
            xhr.addEventListener('abort', function (evt) {
                console.log(droppedFile.name + ' transfer aborted by user!');
                reject(evt);
            });

            xhr.open('PUT', data.url);
            xhr.setRequestHeader('Content-Type', droppedFile.type);
            reader.onload = function (evt) {
                xhr.send(evt.target.result);
            };
            reader.readAsArrayBuffer(droppedFile);
        });
    }

    async deleteAttachment(fileId) {
        this._enforceInitialized();
        if (this.saving) return Promise.resolve(this.data);
        let apiClient = this;
        this.saving = true;

        try {
            let data = await ContentService.deleteAttachment(this.contentId, fileId);
            apiClient.saving = false;
            return apiClient._readJSONResponse(data);
        } catch (err) {
            console.log('Error', err);
            return apiClient.load();
        }
    }
}

export class CategoryApiClient extends ApiClient {
    /**
     * @param {object} environment configuration
    
  
     * @param {string} contentId (Optional)
     * @param {string} groupId (Optional)
     * @param {number} version
     */

    constructor(contentId, groupId, version = -1) {
        super(contentId, groupId, version, 'category');
    }

    /**
     *
     * @param {Array<string>} children
     * @param {Object<string,IconLocal | IIconImage >} images
     * @param {Object<string,string>} properties
     * @param {string} landingPage
     * @param {string} landingPageStyle
     */
    setDefinition(children, images, properties, landingPage, landingPageStyle) {
        this._enforceInitialized();
        if (this.saving) return Promise.resolve(this.data);
        let apiClient = this;
        this.saving = true;

        return ContentService.setContentDefinition(this.contentId, {
            groupId: this.groupId,
            version: this.version,
            children,
            images,
            properties,
            landingPage,
            landingPageStyle,
        })
            .then(function (data) {
                apiClient.saving = false;
                return apiClient._readJSONResponse(data);
            })
            .catch((err) => {
                return apiClient.load();
            });
        // TODO: crida add section metadocument
    }
}
